import React, { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import api, { getApiErrorMessage } from '../../api';
import CopyButton from '../CopyButton';
import { FileIcon, TYPE_LABELS, formatDate, formatSize } from './shareUtils';
import './Share.css';

const REASONS = [
    ['illegal', 'Запрещённая информация'],
    ['malware', 'Вирус или опасный файл'],
    ['copyright', 'Нарушены авторские права'],
    ['spam', 'Реклама, спам или мошенничество'],
    ['other', 'Другое'],
];

/** Жалоба на содержимое: файл сразу перестаёт выдаваться, пока модератор не посмотрит. */
function Complaint({ token }) {
    const [open, setOpen] = useState(() => window.location.hash === '#complaint');
    const [reason, setReason] = useState('illegal');
    const [text, setText] = useState('');
    const [state, setState] = useState('idle');
    const [error, setError] = useState('');

    const send = async (e) => {
        e.preventDefault();
        setState('sending');
        setError('');
        try {
            await api.post(`/public/presentations/${token}/complaint`, { reason, text: text.trim() || undefined });
            setState('done');
        } catch (err) {
            setError(getApiErrorMessage(err, 'Не удалось отправить жалобу'));
            setState('idle');
        }
    };

    if (state === 'done') {
        return <p className="share-complaint__done" role="status">Спасибо. Файл скрыт, пока модератор его не проверит.</p>;
    }
    if (!open) {
        return <button type="button" className="share-complaint__toggle" onClick={() => setOpen(true)}>Пожаловаться на содержимое</button>;
    }
    return (
        <form className="share-complaint" onSubmit={send}>
            <h2>Пожаловаться на презентацию</h2>
            {REASONS.map(([value, label]) => (
                <label key={value} className="share-check">
                    <input type="radio" name="reason" checked={reason === value} onChange={() => setReason(value)} />
                    <span>{label}</span>
                </label>
            ))}
            <textarea
                className="share-complaint__text" rows={3} maxLength={2000} value={text}
                onChange={(e) => setText(e.target.value)} placeholder="Что не так — например, номер слайда (необязательно)"
            />
            {error && <p className="share-card__error" role="alert">{error}</p>}
            <div className="share-card__actions">
                <button type="submit" className="btn btn--secondary" disabled={state === 'sending'}>{state === 'sending' ? 'Отправляем…' : 'Отправить жалобу'}</button>
                <button type="button" className="share-card__skip" onClick={() => setOpen(false)}>Отмена</button>
            </div>
        </form>
    );
}

/** Страница из письма: скачать презентацию и промокод на СВОД. */
function PresentationDownload() {
    const { token } = useParams();
    const [params] = useSearchParams();
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [autoStarted, setAutoStarted] = useState(false);
    const started = useRef(false);

    useEffect(() => {
        document.title = 'Презентация — СВОД';
        api.get(`/public/presentations/${token}`)
            .then((res) => setData(res.data))
            .catch(() => setError('Ссылка недействительна. Проверьте, что она скопирована из письма целиком.'));
    }, [token]);

    const fileUrl = `${api.defaults.baseURL || ''}/public/presentations/${encodeURIComponent(token)}/file`;

    // Пришли по кнопке «Скачать презентацию» из письма — начинаем скачивание сразу, страница остаётся открытой
    useEffect(() => {
        if (!data?.available || params.get('download') !== '1' || started.current) return;
        started.current = true;
        setAutoStarted(true);
        window.location.assign(fileUrl);
    }, [data, params, fileUrl]);
    const register = data?.promo_code
        ? `/login?mode=register&promo=${encodeURIComponent(data.promo_code)}&utm_source=presentation&utm_medium=page`
        : '/login?mode=register&utm_source=presentation&utm_medium=page';

    return (
        <div className="share-page">
            <div className="share-page__inner">
                <div className="share-page__brand">СВОД</div>
                {error && <div className="share-card"><p>{error}</p></div>}
                {!data && !error && <p className="share-muted">Загрузка…</p>}
                {data && (
                    <div className="share-card">
                        <h1>Презентация с мероприятия</h1>
                        {data.source_title && <p className="share-page__source">«{data.source_title}»</p>}
                        {data.available ? (
                            <>
                                <div className="share-file">
                                    <FileIcon />
                                    <div>
                                        <b>{data.title}</b>
                                        <span>{TYPE_LABELS[data.file_type] || ''} · {formatSize(data.size)} · доступна до {formatDate(data.expires_at)}</span>
                                    </div>
                                </div>
                                {autoStarted && (
                                    <p className="share-autostart" role="status">Скачивание началось. Если файл не появился — нажмите кнопку ниже.</p>
                                )}
                                <a className="btn btn--primary share-download" href={fileUrl}>{autoStarted ? 'Скачать ещё раз' : 'Скачать презентацию'}</a>
                            </>
                        ) : (
                            <p>
                                {data.status === 'review' || data.status === 'checking'
                                    ? 'Презентация временно недоступна: она проверяется. Попробуйте позже.'
                                    : data.title
                                        ? 'Срок хранения презентации закончился или организатор её удалил.'
                                        : 'Спикер ещё не загрузил презентацию — мы пришлём письмо, как только она появится.'}
                            </p>
                        )}
                        {data.title && (
                            <div className="share-disclaimer" id="complaint">
                                <p>
                                    Файл загрузил организатор мероприятия — он отвечает за его содержимое. СВОД только передаёт файл
                                    и не несёт ответственности за его содержание. Перед отправкой файл автоматически проверяется
                                    на опасное содержимое, но открывайте его так же внимательно, как любое вложение.
                                </p>
                                {data.available && <Complaint token={token} />}
                            </div>
                        )}
                        <div className="share-promo">
                            <h2>Понравился интерактив? Проводите свои опросы в СВОД</h2>
                            <p>Опросы по QR-коду, результаты на экране в реальном времени, облако слов, вопросы из зала и ИИ-отчёт по ответам.</p>
                            {data.promo_code && (
                                <>
                                    <p>Ваша скидка {data.promo_discount}% на первую оплату тарифа «Расширенный»:</p>
                                    <div className="share-promo__code">
                                        <input readOnly value={data.promo_code} aria-label="Промокод" onFocus={(e) => e.target.select()} />
                                        <CopyButton text={data.promo_code} className="btn btn--secondary btn--sm" />
                                    </div>
                                </>
                            )}
                            <a className="btn btn--secondary" href={register}>Попробовать СВОД бесплатно</a>
                            <small>
                                {data.promo_ends_at && <>Промокод действует до {formatDate(data.promo_ends_at)}. </>}
                                Для регистрации нужна почта в зоне .ru или .рф.
                            </small>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default PresentationDownload;
